import ImagingModule from "../imaging";
import {usePatient} from "../PatientContext";
import ClinicUsers from "../ClinicUsers";
import {
    useState,
    useEffect,
    useMemo,
    useRef
} from "react";

import MobileHeader
from "./MobileHeader";

import MobileNotificationsPanel
from "./MobileNotificationsPanel";

import MobileDashboard
from "./MobileDashboard";

import MobileBottomNav
from "./MobileBottomNav";

import MobilePatients
from "./MobilePatients";

import MobilePacienteDetalle
from "./MobilePacienteDetalle";

import MobileCalendar
from "./MobileCalendar";

import MobileWhatsApp
from "./MobileWhatsApp";

import VexaDentAI from "../VexaDentAI";
import ActivityLogs from "../ActivityLogs";
import { canAccessMobileSection, resolveMobilePermissions } from "./mobileNavigation";

import Finanzas
from "../Finanzas";

import MobileSettingsPanel
from "./MobileSettingsPanel";

import OnboardingTour
from "../onboarding/OnboardingTour";

import {
    useClinic
} from "../context/ClinicContext";

import {
    Scan,
    WalletCards,
    UsersRound,
    Settings2,
    Activity,
    LogOut
} from "lucide-react";

export default function MobileApp(props) {

    const {
        clinicId,
        currentUser
    } = useClinic();

    const permissions = useMemo(
        () => resolveMobilePermissions(clinicId, currentUser, props.userData),
        [clinicId, currentUser, props.userData]
    );



    // =========================
    // NAVEGACIÓN
    // =========================

    const [
        requestedSection,
        setSection
    ] = useState(
        "inicio"
    );

    const section = canAccessMobileSection(requestedSection, permissions)
        ? requestedSection
        : "mas";

    const navigate = nextSection => {
        if (canAccessMobileSection(nextSection, permissions)) {
            setSection(nextSection);
        }
    };

    const [
        notificationsOpen,
        setNotificationsOpen
    ] = useState(false);

    const [
        notificationUnreadCount,
        setNotificationUnreadCount
    ] = useState(0);

    // =========================
    // PULL TO REFRESH
    // =========================

    const contentRef = useRef(null);

    const [
        pullDistance,
        setPullDistance
    ] = useState(0);

    const [
        refreshing,
        setRefreshing
    ] = useState(false);

    const pullStartY = useRef(0);

    const isPulling = useRef(false);
    const refreshTimeoutRef = useRef(null);

    const PULL_THRESHOLD = 140;


    const iniciarPullRefresh = event => {

        if (refreshing) {
            return;
        }

        const contenido = contentRef.current;

        if (!contenido) {
            return;
        }

        if (contenido.scrollTop > 0) {
            return;
        }

        const elemento = event.target;

        if (event.touches.length !== 1 || elemento?.closest?.(
            'form, input, textarea, select, button, a, canvas, [contenteditable="true"], [role="dialog"], [aria-modal="true"], [style*="position: fixed"]'
        )) {
            return;
        }

        let ancestor = elemento;
        while (ancestor && ancestor !== contenido) {
            if (ancestor.scrollTop > 0) return;
            ancestor = ancestor.parentElement;
        }

        const touch = event.touches[0];

        if (!touch) {
            return;
        }

        pullStartY.current =
            touch.clientY;

        isPulling.current = true;

    };


    const moverPullRefresh = event => {

        if (
            !isPulling.current ||
            refreshing
        ) {
            return;
        }

        const contenido = contentRef.current;

        if (!contenido) {
            return;
        }

        if (contenido.scrollTop > 0) {

            isPulling.current = false;

            setPullDistance(0);

            return;

        }

        const touch = event.touches[0];
        if (!touch || event.touches.length !== 1) {
            isPulling.current = false;
            setPullDistance(0);
            return;
        }

        const distancia =
            touch.clientY -
            pullStartY.current;

        if (distancia <= 0) {

            setPullDistance(0);

            return;

        }

        const distanciaLimitada =
            Math.min(
                distancia * 0.55,
                180
            );

        setPullDistance(
            distanciaLimitada
        );

    };


    const cancelarPullRefresh = () => {
        isPulling.current = false;
        setPullDistance(0);
    };

    const terminarPullRefresh = () => {

        if (
            !isPulling.current ||
            refreshing
        ) {
            return;
        }

        isPulling.current = false;

        if (
            pullDistance >=
            PULL_THRESHOLD
        ) {

            setRefreshing(true);

            setPullDistance(
                PULL_THRESHOLD
            );

            refreshTimeoutRef.current = setTimeout(() => {

                const url =
                    new URL(
                        window.location.href
                    );

                url.searchParams.set(
                    "refresh",
                    Date.now().toString()
                );

                window.location.replace(
                    url.toString()
                );

            }, 350);

            return;

        }

        setPullDistance(0);

    };

    // =========================
    // PACIENTE SELECCIONADO
    // =========================

    const {selectedPatient,setSelectedPatient}=usePatient();

    // =========================
    // RENDER
    // =========================

    useEffect(() => {

        const style =
            document.createElement("style");

        style.innerHTML = `

            @keyframes vexadentRefreshSpin {

                from {
                    transform:
                        rotate(0deg);
                }

                to {
                    transform:
                        rotate(360deg);
                }

            }

            @keyframes vexadentLogoReveal {

                0% {
                    opacity:
                        0;

                    transform:
                        scale(0.72);
                }

                55% {
                    opacity:
                        1;

                    transform:
                        scale(1.06);
                }

                100% {
                    opacity:
                        1;

                    transform:
                        scale(1);
                }

            }

            @keyframes vexadentLogoFloat {

                0% {
                    transform:
                        translateY(0);
                }

                50% {
                    transform:
                        translateY(-7px);
                }

                100% {
                    transform:
                        translateY(0);
                }

            }

            @keyframes vexadentGlow {

                0% {
                    filter:
                        drop-shadow(
                            0 0 0
                            rgba(37,99,235,0)
                        );
                }

                50% {
                    filter:
                        drop-shadow(
                            0 0 28px
                            rgba(37,99,235,0.28)
                        );
                }

                100% {
                    filter:
                        drop-shadow(
                            0 0 0
                            rgba(37,99,235,0)
                        );
                }

            }

            @keyframes vexadentNameReveal {

                0% {
                    opacity:
                        0;

                    transform:
                        translateY(14px);

                    letter-spacing:
                        0.28em;
                }

                100% {
                    opacity:
                        1;

                    transform:
                        translateY(0);

                    letter-spacing:
                        -0.02em;
                }

            }

            @keyframes vexadentSubtitleReveal {

                0% {
                    opacity:
                        0;

                    transform:
                        translateY(10px);
                }

                100% {
                    opacity:
                        1;

                    transform:
                        translateY(0);
                }

            }

            @keyframes vexadentLineReveal {

                0% {
                    width:
                        0;

                    opacity:
                        0;
                }

                100% {
                    width:
                        150px;

                    opacity:
                        1;
                }

            }

            @keyframes vexadentSplashOut {

                from {
                    opacity:
                        1;
                }

                to {
                    opacity:
                        0;
                }

            }

            @keyframes vexadentLoadingPulse {

                0% {
                    opacity: 0.35;
                }

                50% {
                    opacity: 1;
                }

                100% {
                    opacity: 0.35;
                }

            }

            @keyframes vexadentLoadingDots {

                0% {
                    opacity: 0.25;
                }

                50% {
                    opacity: 1;
                }

                100% {
                    opacity: 0.25;
                }

            }

        `;

        document.head.appendChild(
            style
        );

        return () => {

            document.head.removeChild(
                style
            );

        };

    }, []);

    useEffect(() => {

        isPulling.current = false;
        setPullDistance(0);
        setRefreshing(false);
        window.clearTimeout(refreshTimeoutRef.current);

        if (!contentRef.current) {
            return;
        }

        contentRef.current.scrollTop = 0;

        return () => window.clearTimeout(refreshTimeoutRef.current);

    }, [section]);

    if (
        !clinicId ||
        !props.clinicData
    ) {

        return (

            <div
                style={{
                    position:
                        "fixed",

                    inset:
                        0,

                    zIndex:
                        99999,

                    background:
                        "#ffffff",

                    display:
                        "flex",

                    flexDirection:
                        "column",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",

                    fontFamily:
                        "Inter, system-ui, Arial",

                    overflow:
                        "hidden"
                }}
            >

                <div
                    style={{
                        display:
                            "flex",

                        flexDirection:
                            "column",

                        alignItems:
                            "center",

                        justifyContent:
                            "center",

                        transform:
                            "translateY(-12px)"
                    }}
                >

                    <div
                        style={{
                            position:
                                "relative",

                            width:
                                150,

                            height:
                                150,

                            display:
                                "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "center",

                            animation:
                                "vexadentLogoReveal 1.15s cubic-bezier(.22,1,.36,1) both, vexadentLogoFloat 3.8s ease-in-out 1.15s infinite, vexadentGlow 2.8s ease-in-out 1.15s infinite"
                        }}
                    >

                        <div
                            style={{
                                position:
                                    "absolute",

                                width:
                                    118,

                                height:
                                    118,

                                borderRadius:
                                    "50%",

                                background:
                                    "radial-gradient(circle, rgba(37,99,235,0.13) 0%, rgba(20,184,166,0.08) 42%, rgba(255,255,255,0) 72%)",

                                filter:
                                    "blur(12px)"
                            }}
                        />

                        <img
                            src="/favicon.png"
                            alt="VexaDent"
                            style={{
                                position:
                                    "relative",

                                width:
                                    128,

                                height:
                                    128,

                                objectFit:
                                    "contain",

                                display:
                                    "block"
                            }}
                        />

                    </div>

                    <div
                        style={{
                            marginTop:
                                24,

                            fontSize:
                                42,

                            lineHeight:
                                1,

                            fontWeight:
                                800,

                            color:
                                "#0f2747",

                            animation:
                                "vexadentNameReveal 0.9s cubic-bezier(.22,1,.36,1) 0.45s both",

                            whiteSpace:
                                "nowrap"
                        }}
                    >

                        <span
                            style={{
                                color:
                                    "#123b6d"
                            }}
                        >
                            Vexa
                        </span>

                        <span
                            style={{
                                color:
                                    "#18b8b5"
                            }}
                        >
                            Dent
                        </span>

                    </div>

                    <div
                        style={{
                            marginTop:
                                12,

                            fontSize:
                                11,

                            fontWeight:
                                600,

                            letterSpacing:
                                "0.22em",

                            color:
                                "#64748b",

                            textAlign:
                                "center",

                            animation:
                                "vexadentSubtitleReveal 0.8s ease-out 0.9s both",

                            whiteSpace:
                                "nowrap"
                        }}
                    >
                        GESTIÓN DENTAL INTELIGENTE
                    </div>

                    <div
                        style={{
                            marginTop:
                                18,

                            height:
                                2,

                            borderRadius:
                                999,

                            background:
                                "linear-gradient(90deg, #2563eb, #14b8a6)",

                            animation:
                                "vexadentLineReveal 0.9s cubic-bezier(.22,1,.36,1) 1.15s both"
                        }}
                    />

                    <div
                        style={{
                            marginTop:
                                18,

                            display:
                                "flex",

                            alignItems:
                                "center",

                            gap:
                                5,

                            fontSize:
                                10,

                            fontWeight:
                                600,

                            letterSpacing:
                                "0.18em",

                            color:
                                "#94a3b8",

                            animation:
                                "vexadentLoadingPulse 1.6s ease-in-out infinite",

                            whiteSpace:
                                "nowrap"
                        }}
                    >

                        <span>
                            CARGANDO VEXADENT
                        </span>

                        <span
                            style={{
                                display:
                                    "inline-flex",

                                gap:
                                    3
                            }}
                        >

                            <span
                                style={{
                                    animation:
                                        "vexadentLoadingDots 1.2s ease-in-out infinite"
                                }}
                            >
                                •
                            </span>

                            <span
                                style={{
                                    animation:
                                        "vexadentLoadingDots 1.2s ease-in-out 0.2s infinite"
                                }}
                            >
                                •
                            </span>

                            <span
                                style={{
                                    animation:
                                        "vexadentLoadingDots 1.2s ease-in-out 0.4s infinite"
                                }}
                            >
                                •
                            </span>

                        </span>

                    </div>

                </div>

            </div>

        );

    }

    return (

        <div
            className="vd-mobile-shell"
            style={{

                minHeight:
                    "100vh",

                background:
                    "#f3f4f6",

                display:
                    "flex",

                flexDirection:
                    "column"

            }}
        >

            {/* =========================
                HEADER
            ========================= */}

            <MobileHeader
                clinicData={
                    props.clinicData
                }

                onNotifications={() =>
                    setNotificationsOpen(
                        true
                    )
                }

                notificationCount={
                    notificationUnreadCount
                }
            />

            <MobileNotificationsPanel
                permissions={permissions}
                open={
                    notificationsOpen
                }

                onClose={() =>
                    setNotificationsOpen(
                        false
                    )
                }

                onNavigate={
                    navigate
                }

                onUnreadCountChange={
                    setNotificationUnreadCount
                }
            />


            {/* =========================
                CONTENIDO
            ========================= */}

            <div
                ref={contentRef}
                className="vd-mobile-content"
                role="main"
                aria-label="Contenido móvil"
                onTouchStart={
                    iniciarPullRefresh
                }
                onTouchMove={
                    moverPullRefresh
                }
                onTouchEnd={
                    terminarPullRefresh
                }
                onTouchCancel={cancelarPullRefresh}
                style={{
                    flex: 1,
                    paddingBottom: 90,
                    overflowY: "auto",
                    overscrollBehaviorY: "contain",
                    WebkitOverflowScrolling:
                        "touch",
                    position: "relative"
                }}
            >

                {/* =========================
                    PULL TO REFRESH
                ========================= */}

                {
                    (
                        pullDistance > 0 ||
                        refreshing
                    ) && (

                        <div
                            role="status"
                            aria-live="polite"
                            aria-label={refreshing ? "Actualizando VexaDent" : "Desliza para actualizar"}
                            style={{
                                height:
                                    refreshing
                                        ? 72
                                        : pullDistance,
                                display:"flex",
                                alignItems:"center",
                                justifyContent:"center",
                                overflow:"hidden",
                                transition:
                                    isPulling.current
                                        ? "none"
                                        : "height 0.2s ease",
                                boxSizing:"border-box",
                                color:"#2563eb",
                                flexDirection:"column",
                                gap:4
                            }}
                        >

                            <div
                                style={{
                                    width:30,
                                    height:30,
                                    borderRadius:"50%",
                                    border:
                                        "3px solid #dbeafe",
                                    borderTopColor:
                                        "#2563eb",
                                    transform:
                                        refreshing
                                            ? "rotate(360deg)"
                                            : `rotate(${Math.min(
                                                pullDistance * 3,
                                                270
                                            )}deg)`,
                                    transition:
                                        refreshing
                                            ? "transform 0.7s linear"
                                            : "none",
                                    animation:
                                        refreshing
                                            ? "vexadentRefreshSpin 0.8s linear infinite"
                                            : "none",
                                    boxSizing:"border-box"
                                }}
                            />

                            <div
                                style={{
                                    fontSize:12,
                                    fontWeight:700,
                                    color:"#2563eb",
                                    lineHeight:1.2
                                }}
                            >
                                {
                                    refreshing
                                        ? "Actualizando VexaDent..."
                                        : pullDistance >=
                                          PULL_THRESHOLD
                                            ? "Suelta para actualizar"
                                            : "Desliza para actualizar"
                                }
                            </div>

                        </div>

                    )
                }


                {/* =========================
                    INICIO
                ========================= */}

                {
                    section ===
                    "inicio" && (

                        <MobileDashboard
                            permissions={permissions}
                            setSection={
                                navigate
                            }
                        />

                    )
                }

                {/* =========================
                    VEXADENT AI
                ========================= */}

                {
                    section === "vexadent-ai" && (
                        <VexaDentAI mobile />
                    )
                }


                {/* =========================
                    PACIENTES
                ========================= */}

                {
                    section ===
                    "pacientes" && (

                        selectedPatient

                            ? (

                                <MobilePacienteDetalle

                                    paciente={
                                        selectedPatient
                                    }

                                    onBack={
                                        () =>
                                            setSelectedPatient(
                                                null
                                            )
                                    }

                                />

                            )

                            : (

                                <MobilePatients

                                    onSelectPatient={
                                        setSelectedPatient
                                    }

                                />

                            )

                    )
                }


                {/* =========================
                    CITAS
                ========================= */}

                {
                    section ===
                    "citas" && (

                        <MobileCalendar />

                    )
                }


                {/* =========================
                    FINANZAS
                ========================= */}

                {
                    section ===
                    "finanzas" && (

                        <Finanzas />

                    )
                }

                {/* =========================
                    AJUSTES
                ========================= */}

                {
                    section === "ajustes" && (
                        <div
                        style={{
                            width: "100%",
                            padding: "16px",
                            paddingBottom: 30,
                            boxSizing: "border-box"
                        }}
                        >
                        <MobileSettingsPanel />

                        <div
                            style={{
                            marginTop: 24,
                            paddingTop: 20,
                            borderTop: "1px solid #e5e7eb"
                            }}
                        >
                            <button
                            type="button"
                            onClick={props.logout}
                            style={{
                                width: "100%",
                                minHeight: 58,
                                padding: "15px 18px",
                                border: "1px solid #fecaca",
                                borderRadius: 16,
                                background: "#fff1f2",
                                color: "#dc2626",
                                fontSize: 16,
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                cursor: "pointer",
                                boxSizing: "border-box",
                                boxShadow: "0 2px 8px rgba(220, 38, 38, 0.08)"
                            }}
                            >
                            <LogOut
                                size={21}
                                strokeWidth={2.4}
                            />
                            Cerrar sesión
                            </button>
                        </div>
                        </div>
                    )
                }


                {section === "imagenologia" && <ImagingModule key={clinicId}/> }

                {section === "usuarios" && <ClinicUsers key={clinicId}/>}

                {/* =========================
                    WHATSAPP
                ========================= */}

                {
                    section === "logs" && (
                        <ActivityLogs mobile onBack={() => navigate("mas")} />
                    )
                }

                {
                    section ===
                    "whatsapp" && (

                        <MobileWhatsApp

                            onSelectPatient={
                                paciente => {

                                    setSelectedPatient(
                                        paciente
                                    );

                                    navigate(
                                        "pacientes"
                                    );

                                }
                            }

                        />

                    )
                }


                {/* =========================
                    MÁS
                ========================= */}

                {
                    section ===
                    "mas" && (

                        <div
                            style={{
                                minHeight:
                                    "100vh",

                                padding:
                                    "18px 16px 110px",

                                background:
                                    "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",

                                boxSizing:
                                    "border-box"
                            }}
                        >

                            <div
                                style={{
                                    marginBottom:22,
                                    padding:
                                        "4px 2px"
                                }}
                            >

                                <div
                                    style={{
                                        fontSize:23,
                                        fontWeight:750,
                                        color:"#0f172a",
                                        lineHeight:1.15,
                                        letterSpacing:
                                            "-0.4px"
                                    }}
                                >

                                    Más

                                </div>

                                <div
                                    style={{
                                        fontSize:13,
                                        color:"#64748b",
                                        marginTop:5
                                    }}
                                >

                                    Herramientas y configuración

                                </div>

                            </div>


                            <div
                                style={{
                                    display:"flex",
                                    flexDirection:"column",
                                    gap:10
                                }}
                            >

                                {/* FINANZAS */}

                                {permissions.finanzas && (
                                <button

                                    type="button"
                                    data-tour="mobile-finances"

                                    onClick={() =>
                                        setSection(
                                            "finanzas"
                                        )
                                    }

                                    style={{
                                        width:"100%",
                                        padding:"16px",
                                        border:
                                            "1px solid #e8edf3",
                                        borderRadius:20,
                                        background:"#fff",
                                        textAlign:"left",
                                        cursor:"pointer",
                                        boxShadow:
                                            "0 3px 10px rgba(15,23,42,0.04)",
                                        display:"flex",
                                        alignItems:"center",
                                        gap:14,
                                        boxSizing:"border-box"
                                    }}
                                >

                                    <div
                                        style={{
                                            width:46,
                                            height:46,
                                            borderRadius:16,
                                            background:"#eff6ff",
                                            display:"flex",
                                            alignItems:"center",
                                            justifyContent:"center",
                                            flexShrink:0
                                        }}
                                    >

                                        <WalletCards
                                            size={22}
                                            strokeWidth={2}
                                            color="#2563eb"
                                        />

                                    </div>

                                    <div>

                                        <div
                                            style={{
                                                fontSize:15,
                                                fontWeight:700,
                                                color:"#0f172a"
                                            }}
                                        >
                                            Finanzas
                                        </div>

                                        <div
                                            style={{
                                                fontSize:13,
                                                color:"#64748b",
                                                marginTop:3
                                            }}
                                        >
                                            Pagos, cuentas y movimientos
                                        </div>

                                    </div>

                                </button>


                                )}

                                {permissions.imagenologia && <button data-tour="mobile-imaging" type="button" onClick={()=>setSection("imagenologia")} style={{width:"100%",padding:16,border:"1px solid #e8edf3",borderRadius:20,background:"#fff",textAlign:"left",display:"flex",alignItems:"center",gap:14,cursor:"pointer"}}><div style={{width:46,height:46,borderRadius:16,background:"#eff6ff",display:"grid",placeItems:"center",color:"#2563eb"}}><Scan size={22}/></div><div><div style={{fontSize:15,fontWeight:700,color:"#0f172a"}}>Imagenología</div><div style={{fontSize:13,color:"#64748b",marginTop:3}}>Tomografías, mediciones y notas</div></div></button>}

                                {/* USUARIOS */}

                                {permissions.usuarios && (
                                <button

                                    type="button"
                                    data-tour="mobile-users"

                                    onClick={() =>
                                        setSection(
                                            "usuarios"
                                        )
                                    }

                                    style={{
                                        width:"100%",
                                        padding:"16px",
                                        border:
                                            "1px solid #e8edf3",
                                        borderRadius:20,
                                        background:"#fff",
                                        textAlign:"left",
                                        cursor:"pointer",
                                        boxShadow:
                                            "0 3px 10px rgba(15,23,42,0.04)",
                                        display:"flex",
                                        alignItems:"center",
                                        gap:14,
                                        boxSizing:"border-box"
                                    }}
                                >

                                    <div
                                        style={{
                                            width:46,
                                            height:46,
                                            borderRadius:16,
                                            background:"#eff6ff",
                                            display:"flex",
                                            alignItems:"center",
                                            justifyContent:"center",
                                            flexShrink:0
                                        }}
                                    >

                                        <UsersRound
                                            size={22}
                                            strokeWidth={2}
                                            color="#2563eb"
                                        />

                                    </div>

                                    <div>

                                        <div
                                            style={{
                                                fontSize:15,
                                                fontWeight:700,
                                                color:"#0f172a"
                                            }}
                                        >
                                            Usuarios
                                        </div>

                                        <div
                                            style={{
                                                fontSize:13,
                                                color:"#64748b",
                                                marginTop:3
                                            }}
                                        >
                                            Equipo y permisos
                                        </div>

                                    </div>

                                </button>


                                )}

                                {/* AJUSTES */}

                                {permissions.configuracion && (
                                <button

                                    type="button"
                                    data-tour="mobile-settings"

                                    onClick={() =>
                                        setSection(
                                            "ajustes"
                                        )
                                    }

                                    style={{
                                        width:"100%",
                                        padding:"16px",
                                        border:
                                            "1px solid #e8edf3",
                                        borderRadius:20,
                                        background:"#fff",
                                        textAlign:"left",
                                        cursor:"pointer",
                                        boxShadow:
                                            "0 3px 10px rgba(15,23,42,0.04)",
                                        display:"flex",
                                        alignItems:"center",
                                        gap:14,
                                        boxSizing:"border-box"
                                    }}
                                >

                                    <div
                                        style={{
                                            width:46,
                                            height:46,
                                            borderRadius:16,
                                            background:"#eff6ff",
                                            display:"flex",
                                            alignItems:"center",
                                            justifyContent:"center",
                                            flexShrink:0
                                        }}
                                    >

                                        <Settings2
                                            size={22}
                                            strokeWidth={2}
                                            color="#2563eb"
                                        />

                                    </div>

                                    <div>

                                        <div
                                            style={{
                                                fontSize:15,
                                                fontWeight:700,
                                                color:"#0f172a"
                                            }}
                                        >
                                            Ajustes
                                        </div>

                                        <div
                                            style={{
                                                fontSize:13,
                                                color:"#64748b",
                                                marginTop:3
                                            }}
                                        >
                                            Configuración de VexaDent
                                        </div>

                                    </div>

                                </button>


                                )}

                                {/* ACTIVIDAD */}

                                {permissions.logs && (
                                <button

                                    type="button"
                                    onClick={() => navigate("logs")}

                                    style={{
                                        width:"100%",
                                        padding:"16px",
                                        border:
                                            "1px solid #e8edf3",
                                        borderRadius:20,
                                        background:"#fff",
                                        textAlign:"left",
                                        cursor:"pointer",
                                        boxShadow:
                                            "0 3px 10px rgba(15,23,42,0.04)",
                                        display:"flex",
                                        alignItems:"center",
                                        gap:14,
                                        boxSizing:"border-box"
                                    }}
                                >

                                    <div
                                        style={{
                                            width:46,
                                            height:46,
                                            borderRadius:16,
                                            background:"#eff6ff",
                                            display:"flex",
                                            alignItems:"center",
                                            justifyContent:"center",
                                            flexShrink:0
                                        }}
                                    >

                                        <Activity
                                            size={22}
                                            strokeWidth={2}
                                            color="#2563eb"
                                        />

                                    </div>

                                    <div>

                                        <div
                                            style={{
                                                fontSize:15,
                                                fontWeight:700,
                                                color:"#0f172a"
                                            }}
                                        >
                                            Actividad
                                        </div>

                                        <div
                                            style={{
                                                fontSize:13,
                                                color:"#64748b",
                                                marginTop:3
                                            }}
                                        >
                                            Historial de actividad
                                        </div>

                                    </div>

                                </button>

                                )}

                                {/* CERRAR SESIÓN */}

                                <button
                                    type="button"
                                    onClick={props.logout}
                                    style={{
                                        width:"100%",
                                        padding:"16px",
                                        marginTop:8,
                                        border:"1px solid #fecaca",
                                        borderRadius:20,
                                        background:"#fff1f2",
                                        textAlign:"left",
                                        cursor:"pointer",
                                        boxShadow:
                                            "0 3px 10px rgba(220,38,38,0.06)",
                                        display:"flex",
                                        alignItems:"center",
                                        gap:14,
                                        boxSizing:"border-box"
                                    }}
                                >

                                    <div
                                        style={{
                                            width:46,
                                            height:46,
                                            borderRadius:16,
                                            background:"#fee2e2",
                                            display:"flex",
                                            alignItems:"center",
                                            justifyContent:"center",
                                            flexShrink:0
                                        }}
                                    >

                                        <LogOut
                                            size={22}
                                            strokeWidth={2.2}
                                            color="#dc2626"
                                        />

                                    </div>

                                    <div>

                                        <div
                                            style={{
                                                fontSize:15,
                                                fontWeight:700,
                                                color:"#dc2626"
                                            }}
                                        >
                                            Cerrar sesión
                                        </div>

                                        <div
                                            style={{
                                                fontSize:13,
                                                color:"#b91c1c",
                                                marginTop:3
                                            }}
                                        >
                                            Salir de VexaDent
                                        </div>

                                    </div>

                                </button>

                            </div>

                        </div>

                    )
                }

            </div>


            {/* =========================
                NAVEGACIÓN INFERIOR
            ========================= */}

            <MobileBottomNav
                permissions={permissions}
                section={section}
                setSection={(nextSection) => {
                    if (nextSection === "pacientes") {
                    setSelectedPatient(null);
                    }

                    navigate(nextSection);
                }}
            />

            <OnboardingTour
                user={props.onboardingUser}
                clinicId={clinicId}
                userData={props.userData}
                isMobile={true}
                currentSection={section}
                onNavigate={navigate}
            />

        </div>

    );

}
                                                        
